import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";

import { pickRandomIndices } from "../../../../utils/listHelper";
import { translationArrayToString } from "../../../models/functions/translation-array-to-string.function";
import { Suggestion } from "./Suggestion/Suggestion";

import "./Suggestions.scss";

interface ISuggestionContent {
  intro: string;
  details: string;
}

interface ISuggestionsProps {
  suggestionCount: number;
  suggestionsTranslationArray: ISuggestionContent[];
  selectSuggestion: (suggestion: string) => void;
  id?: string;
}

const RawSuggestions: FunctionComponent<ISuggestionsProps> = props => {
  const { t } = useTranslation();

  // translated strings for the current language
  const suggestions: ISuggestionContent[] =
    (t(translationArrayToString(props.suggestionsTranslationArray), {
      returnObjects: true,
    }) as ISuggestionContent[]) ?? [];

  // store the chosen indices once, do not reset on language change
  const [chosenIndices, setChosenIndices] = useState<number[] | null>(null);

  useEffect(() => {
    // already chosen indices or there are no suggestions
    if (chosenIndices || !suggestions.length) {
      return;
    }

    // initialize randomly selected indices once when there is suggestions data
    setChosenIndices(pickRandomIndices(suggestions.length, props.suggestionCount ?? 4));
  }, [chosenIndices, suggestions.length, props.suggestionCount]);

  const mappedSuggestions = useMemo(() => {
    const chosenSuggestionIndices = chosenIndices ?? [];

    return chosenSuggestionIndices
      .map(i => suggestions[i])
      .map((suggestion, index) => (
        <div key={`${suggestion.intro}_${suggestion.details}`} className="suggestion-wrapper">
          <Suggestion
            selectSuggestion={props.selectSuggestion}
            intro={suggestion.intro}
            details={suggestion.details}
            id={`${props.id ?? "suggestion"}-${index + 1}`}
          />
        </div>
      ));
  }, [chosenIndices, suggestions, props.selectSuggestion, props.id]);

  return <div className="suggestion-list-wrapper">{mappedSuggestions}</div>;
};

export const Suggestions = observer(RawSuggestions);
